// Bridge between the LaunchDarkly `schedulingBootstrapTimeoutMs` flag (read in
// React via useFlags) and the non-React code on the critical bootstrap path
// (the hand-rolled /me thunk in auth-slice, and the RTK Query siblings). The
// flag value is pushed in once from App.tsx; everything else reads it here.
//
// Kept free of `import.meta` on purpose so it stays importable under ts-jest
// (auth-slice itself is not — see CLAUDE.md). The timeout classifier and signal
// builder live here so they can be unit-tested directly.

// Armed by default so the very first /me on a cold page load is protected
// BEFORE the LaunchDarkly SDK has resolved the flag — that first bootstrap call
// is the one most at risk from a hung socket. LD can override the value, or
// disable it entirely (serve 0), once it initializes.
export const DEFAULT_CRITICAL_TIMEOUT_MS = 12000;

let criticalTimeoutMs = DEFAULT_CRITICAL_TIMEOUT_MS;

// `ms <= 0` (or NaN) disarms the timeout — the LD kill-switch.
export const setCriticalTimeoutMs = (ms: number): void => {
  criticalTimeoutMs = ms > 0 ? ms : 0;
};

// Returns the timeout in ms, or `undefined` when disabled so callers can pass
// it straight to `fetch`/`FetchArgs.timeout` as a no-op.
export const getCriticalTimeoutMs = (): number | undefined =>
  criticalTimeoutMs || undefined;

// Fresh per call so each fetch attempt gets its own deadline. `undefined` when
// the flag is off → callers attach no signal (byte-for-byte today's behavior).
//
// Feature-detected: on a browser without AbortSignal.timeout (pre-2022
// baseline) the unguarded call would throw a synchronous TypeError that the
// /me thunk classifies as non-transient → clearAuthStorage() — the timeout
// meant to protect the session would destroy it. Fall back to no signal
// (today's behavior) instead.
export const buildCriticalTimeoutSignal = (): AbortSignal | undefined => {
  const ms = getCriticalTimeoutMs();
  if (
    !ms ||
    typeof AbortSignal === "undefined" ||
    typeof AbortSignal.timeout !== "function"
  ) {
    return undefined;
  }
  return AbortSignal.timeout(ms);
};

// `AbortSignal.timeout()` rejects fetch with a DOMException named "TimeoutError"
// (NOT "AbortError"). A manual AbortController.abort() yields "AbortError". Match
// both so a timed-out /me is treated as transient and never clears auth tokens.
export const isTimeoutAbort = (err: unknown): boolean => {
  const name = (err as { name?: string } | null | undefined)?.name;
  return name === "TimeoutError" || name === "AbortError";
};
